import {
  Autocomplete,
  FilledInputProps,
  FormControl,
  FormHelperText,
  InputBaseProps,
  InputLabel,
  InputProps as StandardInputProps,
  OutlinedInputProps,
  Select,
  SelectProps,
  SxProps,
  TextField,
  Theme,
} from '@mui/material';
import { InputHTMLAttributes, PropsWithChildren, ReactNode } from 'react';
import { Controller, FieldValues, RegisterOptions, UseFormReturn } from 'react-hook-form';
import { FieldPath } from 'react-hook-form/dist/types/path/eager';
import { sentenceCase } from 'utils/casing';
import { PropertyOfType } from 'utils/typings';

// Type that allows both strict field paths and dynamic array element paths like `array.${number}.field`
// The first part must be a key of T, ensuring type safety for the array field name
type ArrayElementPath<T extends FieldValues> = `${Extract<keyof T, string>}.${number}.${string}`;
type ArrayElementPathNested<T extends FieldValues> = `${Extract<
  keyof T,
  string
>}.${number}.${string}.${number}.${string}`;

export interface FormTextFieldProps<T extends FieldValues> {
  children?: ReactNode;
  form: UseFormReturn<T>;
  name:
    | (FieldPath<T> & PropertyOfType<T, string | number | null>)
    | ArrayElementPath<T>
    | ArrayElementPathNested<T>;
  label?: string;
  disabled?: boolean;
  minLength?: number;
  maxLength?: number;
  customValidate?: RegExp;
  customValidateMessage?: string;
  /** Extra rule for non-empty values; return a message to fail. */
  customValidateFn?: (value: string) => string | undefined;
  helperText?: ReactNode;
  helperTextComponent?: React.ElementType;
  optional?: true;
  autoFocus?: boolean;
  multiline?: true;
  inputProps?: InputBaseProps['inputProps'];
  InputProps?: Partial<StandardInputProps>;
  SelectProps?: Partial<SelectProps>;
  sx?: SxProps<Theme>;
  helperTextSx?: SxProps<Theme>;
  type?: InputHTMLAttributes<unknown>['type'];
  variant?: 'standard' | 'filled' | 'outlined';
}

type VariantInputProps = {
  standard: StandardInputProps;
  filled: FilledInputProps;
  outlined: OutlinedInputProps;
};

type SelectSlotProps<V extends keyof VariantInputProps = 'outlined'> = Omit<
  NonNullable<SelectProps['slotProps']>,
  'input'
> & {
  input?: Partial<VariantInputProps[V]>;
};

export interface SelectFieldProps<
  T extends FieldValues,
  V extends keyof VariantInputProps = 'outlined',
> {
  children?: ReactNode;
  form: UseFormReturn<T>;
  name:
    | (FieldPath<T> & PropertyOfType<T, string | number | null>)
    | ArrayElementPath<T>
    | ArrayElementPathNested<T>;
  label?: string;
  disabled?: boolean;
  optional?: true;
  autoFocus?: boolean;
  sx?: SxProps<Theme>;
  renderValue?: (v: string) => ReactNode;
  type?: InputHTMLAttributes<unknown>['type'];
  variant?: V;
  slotProps?: SelectSlotProps<V>;
  MenuProps?: SelectProps['MenuProps'];
}

export type FormFieldPath<T extends FieldValues> =
  | (FieldPath<T> & PropertyOfType<T, string | number | null>)
  | ArrayElementPath<T>;

// eslint-disable-next-line @typescript-eslint/ban-types
export const FormTextField = <T extends {}>({
  children,
  form,
  name,
  label,
  disabled,
  minLength,
  maxLength,
  customValidate,
  customValidateMessage,
  customValidateFn,
  helperText,
  helperTextComponent,
  optional,
  autoFocus,
  inputProps,
  InputProps,
  SelectProps,
  multiline,
  sx,
  helperTextSx,
  type,
  variant,
}: PropsWithChildren<FormTextFieldProps<T>>) => {
  const select = !!children;
  const label1 = label ?? sentenceCase(name);

  return (
    <Controller
      control={form.control}
      name={name as FieldPath<T>}
      rules={{
        ...(customValidate && {
          pattern: {
            value: customValidate,
            message: customValidateMessage ?? `Does not match ${customValidate.toString()}`,
          },
        }),
        validate: (value) => {
          if (optional) {
            return value ? customValidateFn?.(value as string) : undefined;
          }
          if (!(value as string)?.trim()) {
            return `${label1} is required`;
          }
          if (!value) {
            return `${label1} is required`;
          }
          return customValidateFn?.(value as string);
        },
      }}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          label={label1}
          fullWidth
          disabled={disabled}
          error={!!fieldState.error}
          helperText={fieldState.error?.message ?? helperText}
          select={select}
          sx={sx}
          FormHelperTextProps={{
            sx: helperTextSx,
            component: helperTextComponent,
          }}
          inputProps={{
            minLength,
            maxLength,
            'data-1p-ignore': '', // Do not show 1password icon for all text fields
            ...inputProps,
          }}
          InputProps={{
            ...InputProps,
          }}
          SelectProps={SelectProps}
          multiline={multiline}
          required={!optional}
          autoFocus={autoFocus}
          margin="normal"
          type={type ?? 'text'}
          variant={variant ?? 'outlined'}
        >
          {children}
        </TextField>
      )}
    />
  );
};

// eslint-disable-next-line @typescript-eslint/ban-types
export const FormSelectField = <T extends {}, V extends keyof VariantInputProps = 'outlined'>({
  children,
  form,
  name,
  label,
  disabled,
  optional,
  autoFocus,
  sx,
  slotProps,
  MenuProps,
  variant,
  renderValue,
}: PropsWithChildren<SelectFieldProps<T, V>>) => {
  const finalLabel = label ?? sentenceCase(name);

  return (
    <Controller
      control={form.control}
      name={name as FieldPath<T>}
      render={({ field, fieldState }) => (
        <FormControl
          fullWidth
          sx={sx}
          error={!!fieldState.error}
          required={!optional}
          margin="normal"
        >
          <InputLabel>{finalLabel}</InputLabel>
          <Select
            {...field}
            label={finalLabel}
            disabled={disabled}
            slotProps={slotProps as SelectProps['slotProps']}
            MenuProps={MenuProps}
            renderValue={renderValue}
            autoFocus={autoFocus}
            variant={variant ?? 'outlined'}
          >
            {children}
          </Select>
          {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  );
};

type AutocompleteProps<T extends FieldValues> = {
  optionsName: FieldPath<T> & PropertyOfType<T, Array<string>>;
  freeSolo?: boolean;
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: string) => React.ReactNode;
  getOptionLabel?: (option: string) => string;
  rules?: RegisterOptions<T>;
};

// eslint-disable-next-line @typescript-eslint/ban-types
export const FormAutocomplete = <T extends FieldValues>(
  props: Omit<AutocompleteProps<T> & FormTextFieldProps<T>, 'multiline'>,
) => {
  const {
    optional,
    label,
    name,
    inputProps,
    InputProps,
    customValidate,
    customValidateMessage,
    minLength,
    maxLength,
    variant,
  } = props;
  const options = props.form.watch(props.optionsName) as string[];

  const labelSentenceCase = label ?? sentenceCase(name);

  return (
    <Controller
      name={props.name as FieldPath<T>}
      control={props.form.control}
      rules={
        props.rules ?? {
          ...(customValidate && {
            pattern: {
              value: customValidate,
              message: customValidateMessage ?? `Does not match ${customValidate.toString()}`,
            },
          }),
          validate: (value) => {
            if (optional) {
              return;
            }
            if (!(value as string)?.trim()) {
              return `${labelSentenceCase} is required`;
            }
            if (!value) {
              return `${labelSentenceCase} is required`;
            }
          },
        }
      }
      render={({ field, fieldState }) => (
        <Autocomplete
          freeSolo={props.freeSolo ?? true}
          value={field.value ?? ''}
          disabled={props.disabled}
          forcePopupIcon={Array.isArray(options) && options.length > 0}
          renderInput={(params) => (
            <TextField
              {...params}
              variant={variant ?? 'outlined'}
              type={props.type ?? 'text'}
              label={props.label ?? sentenceCase(props.name)}
              error={!!fieldState.error}
              helperText={fieldState.error?.message ?? props.helperText}
              margin="normal"
              required={!optional}
              slotProps={{
                htmlInput: {
                  minLength,
                  maxLength,
                  'data-1p-ignore': '', // Do not show 1password icon for all text fields
                  ...(params.inputProps ?? {}),
                  ...(inputProps ?? {}),
                },
                input: {
                  ...(params.InputProps ?? {}),
                  ...(InputProps ?? {}),
                },
              }}
              disabled={props.disabled}
              sx={props.sx}
            />
          )}
          options={options}
          getOptionLabel={props.getOptionLabel}
          multiple={false}
          onChange={(_e, value) => {
            field.onChange(value ?? '');
          }}
          onInputChange={(_e, value) => {
            // For freeSolo mode, update the field value when typing
            if (props.freeSolo ?? true) {
              field.onChange(value);
            }
          }}
          onBlur={field.onBlur}
        />
      )}
    />
  );
};
